import { createAsyncThunk } from "@reduxjs/toolkit";
import type { ClassifierValue } from "../../../../services/api/instances/classificatorApi/types";
import {
  createStorageObjectVersion,
  deleteStorageObjectVersion,
  getAllStorageObjectVersions,
  updateStorageObjectVersion,
} from "../../../../services/api/instances/storageApi/versioningMethods";
import type { RootState } from "../../../../services/redux/types";
import {
  setClassifierObjectVersionsAction,
  setSelectedClassifierObjectVersionAction,
} from "../slice";
import { StorageObjectVersion } from "../../../../services/api/instances/storageApi/types/object_types";
import { getError } from "../../../../services/lib/getError";

export const getAllClassifierObjectVersions = createAsyncThunk(
  "classifier/getAllClassifierObjectVersions",
  async (id: string, { rejectWithValue }) => {
    try {
      return await getAllStorageObjectVersions<ClassifierValue>(id, "classify");
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const createClassifierObjectVersion = createAsyncThunk(
  "classifier/createClassifierObjectVersion",
  async (name: string, ThunkAPI) => {
    try {
      const { selectedClassifier } = (ThunkAPI.getState() as RootState)
        .classifierState;
      const date = new Date();
      const postObj = {
        version_name: `${name}`,
        version_date: date.toISOString(),
      };
      return await createStorageObjectVersion<ClassifierValue>(
        selectedClassifier?.id as string,
        postObj,
        "classify",
      );
    } catch (e: unknown) {
      const message = getError(e);
      return ThunkAPI.rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const updateClassifierObjectVersion = createAsyncThunk(
  "classifier/updateClassifierObjectVersion",
  async (
    postObj: StorageObjectVersion<ClassifierValue>,
    { rejectWithValue },
  ) => {
    try {
      return updateStorageObjectVersion<ClassifierValue>(postObj, "classify");
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const deleteClassifierObjectVersion = createAsyncThunk(
  "classifier/getClassifierObjectVersion",
  async (versionId: string, ThunkAPI) => {
    try {
      const { classifierObjectVersions, selectedClassifier } = (
        ThunkAPI.getState() as RootState
      ).classifierState;
      const id = selectedClassifier?.id;
      const updatedClassifierObjectVersion = classifierObjectVersions.filter(
        (classifier) => classifier.version_id !== versionId,
      );
      const nextVersion = updatedClassifierObjectVersion.at(-1);

      ThunkAPI.dispatch(
        setClassifierObjectVersionsAction(updatedClassifierObjectVersion),
      );
      if (updatedClassifierObjectVersion.length === 0) {
        ThunkAPI.dispatch(setSelectedClassifierObjectVersionAction(null));
      } else {
        ThunkAPI.dispatch(
          setSelectedClassifierObjectVersionAction(
            nextVersion as StorageObjectVersion<ClassifierValue>,
          ),
        );
      }

      return await deleteStorageObjectVersion<ClassifierValue>(
        id as string,
        versionId,
        "classify",
      );
    } catch (e: unknown) {
      const message = getError(e);
      return ThunkAPI.rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);
